import { Film, ImageOff, Play } from 'lucide-react';
import { RemoteImage } from '@/components/remote-image';
import {
  channelLabel,
  firstSharerName,
  formatAbsoluteTitle,
  formatDuration,
  formatRelative,
} from '@/lib/format';
import { cn } from '@/lib/utils';
import type { MediaItem } from '@/types/api';

/** What a screen reader hears for a file: there is no alt text to be had from Slack. */
export function describeMedia(media: MediaItem): string {
  const fallback = media.kind === 'video' ? 'Video' : 'Image';
  const name = media.title ?? media.name ?? fallback;
  const length =
    media.kind === 'video' && media.durationMs !== undefined
      ? ` (video, ${formatDuration(media.durationMs)})`
      : media.kind === 'video' && name !== fallback
        ? ' (video)'
        : '';
  return `${name}${length}, shared by ${firstSharerName(media.firstShare)} in ${channelLabel(media.firstShare)}`;
}

/**
 * The title worth showing on a tile: one somebody wrote or generated. Not the file name Slack
 * copies into the title by default — "IMG_4031.MOV" says nothing a thumbnail does not.
 */
export function mediaHeading(media: MediaItem): string | undefined {
  if (!media.title) return undefined;
  if (media.textSource) return media.title;
  return media.title !== media.name ? media.title : undefined;
}

/**
 * One square in the Media grid. The whole tile is a button that opens the viewer, so the grid is
 * one tab stop per file and every file is reachable without a pointer. A video shows its poster
 * frame with a play badge and its length; nothing plays until it is opened.
 */
export function MediaTile({ media, onOpen }: { media: MediaItem; onOpen: () => void }) {
  const sharer = firstSharerName(media.firstShare);
  const isVideo = media.kind === 'video';
  const heading = mediaHeading(media);

  return (
    <figure className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-sm transition-shadow focus-within:shadow-md hover:shadow-md">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${describeMedia(media)}`}
        className={cn(
          'relative block aspect-square w-full overflow-hidden bg-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        )}
      >
        <RemoteImage
          src={media.thumbnailUrl}
          alt=""
          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
          wrapperClassName="h-full w-full"
          fallback={
            isVideo ? (
              <Film className="h-6 w-6 text-muted-foreground" />
            ) : (
              <ImageOff className="h-6 w-6 text-muted-foreground" />
            )
          }
        />
        {isVideo ? (
          <span
            aria-hidden="true"
            className="absolute bottom-2 left-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-white"
          >
            <Play className="h-3 w-3 fill-current" />
            {media.durationMs !== undefined ? formatDuration(media.durationMs) : 'Video'}
          </span>
        ) : null}
      </button>
      <figcaption className="flex min-w-0 flex-col gap-0.5 px-3 py-2 text-xs">
        {heading ? (
          <span className="line-clamp-2 font-medium text-foreground" title={heading}>
            {heading}
          </span>
        ) : null}
        <span
          className={cn(
            'truncate',
            heading ? 'text-muted-foreground' : 'font-medium text-foreground',
          )}
          title={sharer}
        >
          {sharer}
        </span>
        <span className="flex min-w-0 items-center gap-1 text-muted-foreground">
          <span className="truncate">{channelLabel(media.firstShare)}</span>
          <span aria-hidden="true">·</span>
          <time
            className="shrink-0"
            dateTime={media.firstShare.sharedAt}
            title={formatAbsoluteTitle(media.firstShare.sharedAt)}
          >
            {formatRelative(media.firstShare.sharedAt)}
          </time>
        </span>
      </figcaption>
    </figure>
  );
}
